import type { Priority, Size, StatusCategory } from "@beacon/core/projects"
import type { MouseEvent, ReactNode } from "react"
import { Link } from "react-router"
import { cn } from "@/primitives/utils"
import { AvatarStack, type PersonShown } from "./identity.tsx"
import { Key } from "./key.tsx"
import { opensPeek } from "./ticket-peek.ts"
import {
  BlockedChip,
  EpicChip,
  PriorityIcon,
  SizeChip,
  StatusIcon,
  TypeIcon,
  type TicketKind,
} from "./ticket-glyphs.tsx"
import type { StatusStep } from "./ticket-glyphs.ts"
import { useViewport } from "./viewport.tsx"

/*
 * A ticket as one line of a list, the same wherever tickets are listed: My tickets, Home, an Epic's
 * tickets, a ticket's links. The whole row is a link to the ticket. Its facts sit in fixed slots, so
 * down a list the keys, circles and titles line up, and an empty slot (no Epic, a Story's type)
 * keeps its place rather than letting the next one shift.
 */

/** A ticket's Status as a row shows it: its circle by category and step, and its name. */
type RowStatus = { category: StatusCategory; name: string; step?: StatusStep }

/**
 * A ticket as a `list` row needs it. `size` is its Size, abbreviated on the row and named in full
 * (`name`) in its tooltip, or none while it is unsized. `assignees` are Members by name; `closed`
 * mutes its title.
 */
export type ListedTicket = {
  key: string
  title: string
  status: RowStatus
  priority: Priority
  size: { size: Size; name: string } | null
  type: TicketKind
  blocked: boolean
  blockers?: readonly string[]
  epic?: { key: string; title?: string }
  assignees: readonly PersonShown[]
  closed: boolean
}

/**
 * A ticket as a `link` row needs it. Without a `title` it is one the Member can't read (in another
 * Project), and the row shows only its key.
 */
export type LinkedTicket = { key: string; title?: string; status?: RowStatus }

/**
 * Where the row sits: full bleed on a page (24px in), or inside a panel on it (16px in). In a
 * panel the last row leaves its rule to the panel's own edge, and the avatars ring in the card's
 * colour, which the panel is. Either way the ring takes the row's fill when the row fills.
 */
const insets = {
  page: { row: "px-6", on: "background-row" },
  panel: { row: "px-4 last:border-b-0", on: "card-row" },
} as const

/** Where a row's ticket opens: in place of the page, or in a new browser tab. */
type Opens = "in-place" | "new-tab"

const targets = {
  "in-place": {},
  "new-tab": { target: "_blank", rel: "noopener" },
} as const satisfies Record<Opens, { target?: string; rel?: string }>

/**
 * The keyboard's highlight (from useRowNavigation) and a Tab focus look alike: the hover fill and a
 * bar in the ring colour at the row's left edge. Any list of links that `useRowNavigation` moves
 * through wears it, as the Groups list does. The row is a `group/row`, so what sits on it can
 * follow its fill, as an AvatarStack `on` a `…-row` does.
 */
export const rowHighlight =
  "group/row relative outline-none before:absolute before:inset-y-0 before:left-0 before:w-0.5 hover:bg-accent focus-visible:bg-accent focus-visible:before:bg-ring data-highlighted:bg-accent data-highlighted:before:bg-ring"

/**
 * One ticket in a list. `list` is a list's row, 36px: priority, key, Status circle (its name in a
 * tooltip), title, then any Blocked and Epic chips, the Size (an unsized ticket's slot stays
 * empty), the type (a Bug's or an Epic's; a Story's slot stays empty) and the assignees.
 * `hideEpic` drops the Epic chip where the list is the Epic's own. `inset` is `panel` inside a
 * card, as on Home and an Epic. On `compact` it is two lines, at least 56px: the Status circle and
 * the title, wrapping to a second line before it is cut; then, in `text-xs`, the key, the Status's
 * name, the priority, and only the Size, type, Blocked and Epic chips the ticket has, with the
 * assignees at the end. No slot is kept for what a ticket hasn't. Its `trailing` (a ⋯ standing
 * for a drag, as an Epics tab row's Move to Epic) sits at its end, centred, beside the link; the
 * desk's row, all fixed slots, has none.
 *
 * `link` is a ticket's link to another, 32px: Status circle, key, title, then `trailing` (its ×)
 * revealed on hover. A ticket the Member can't read shows its key alone, and leads nowhere without
 * an `href`.
 *
 * `opens` is where the row's ticket opens: in place of this page (the default), or, as a row inside
 * another ticket does on the desk, in a new browser tab. A list's row with `onPeek` opens its ticket in the
 * Ticket peek on a plain click on the desk layout (`opensPeek`), and its link takes any other, and
 * every tap on `compact`, where a ticket opens as its page. `state` goes with either's link, as a
 * crumb's does, for the page it opens: the view or ticket it was opened from, which leads back to
 * it.
 *
 * Both are `data-row`s, so `useRowNavigation` moves through them with j/k and opens one with Enter.
 */
export function TicketRow(
  props:
    | {
        variant: "list"
        href: string
        state?: unknown
        opens?: Opens
        onPeek?: () => void
        ticket: ListedTicket
        hideEpic?: boolean
        inset?: keyof typeof insets
        trailing?: ReactNode
      }
    | {
        variant: "link"
        href?: string
        state?: unknown
        opens?: Opens
        ticket: LinkedTicket
        trailing?: ReactNode
      },
) {
  if (props.variant === "link") return <LinkRow {...props} />
  return <ListRow {...props} />
}

function ListRow({
  href,
  state,
  opens = "in-place",
  onPeek,
  ticket,
  hideEpic = false,
  inset = "page",
  trailing,
}: {
  href: string
  state?: unknown
  opens?: Opens
  onPeek?: () => void
  ticket: ListedTicket
  hideEpic?: boolean
  inset?: keyof typeof insets
  trailing?: ReactNode
}) {
  const { compact } = useViewport()
  const link = {
    to: href,
    ...(state !== undefined && { state }),
    ...targets[opens],
    ...(onPeek && {
      onClick: (event: MouseEvent) => {
        if (!opensPeek(event, compact)) return
        event.preventDefault()
        onPeek()
      },
    }),
    "data-row": "",
  }
  if (compact) {
    const row = (
      <Link
        {...link}
        className={cn(
          "flex min-h-14 scroll-mt-32 items-start gap-3 border-b border-border-subtle px-4 py-2 text-ui transition-colors duration-100 ease-snappy",
          inset === "panel" && "last:border-b-0",
          trailing !== undefined && "pr-12",
          rowHighlight,
        )}
      >
        {/* As tall as the title's first line, so the circle sits level with it however it wraps. */}
        <span className="flex h-lh w-4 shrink-0 items-center justify-center">
          <StatusIcon
            category={ticket.status.category}
            name={ticket.status.name}
            {...(ticket.status.step && { step: ticket.status.step })}
          />
        </span>
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              "line-clamp-2 wrap-break-word",
              ticket.closed ? "text-muted-foreground" : "text-foreground",
            )}
          >
            {ticket.title}
          </span>
          <span className="mt-1 flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
            {/* One line, never wrapping: the Status's name and the Epic's chip give up width, and
                the glyphs keep theirs. */}
            <span className="flex min-w-0 items-center gap-2">
              <Key value={ticket.key} />
              <span className="min-w-0 truncate">{ticket.status.name}</span>
              <PriorityIcon priority={ticket.priority} standalone />
              {ticket.size && <SizeChip size={ticket.size.size} name={ticket.size.name} />}
              {ticket.type !== "story" && <TypeIcon type={ticket.type} standalone />}
              {ticket.blocked && (
                <BlockedChip {...(ticket.blockers && { blockers: ticket.blockers })} />
              )}
              {!hideEpic && ticket.epic && (
                <EpicChip
                  epicKey={ticket.epic.key}
                  {...(ticket.epic.title !== undefined && { title: ticket.epic.title })}
                  on="card"
                />
              )}
            </span>
            {ticket.assignees.length > 0 && (
              <span className="ml-auto flex shrink-0">
                <AvatarStack people={ticket.assignees} size={20} on={insets[inset].on} />
              </span>
            )}
          </span>
        </span>
      </Link>
    )
    if (trailing === undefined) return row
    // Beside the link rather than in it, since a control can't sit inside a link.
    return (
      <div className="relative">
        {row}
        <span className="absolute inset-y-0 right-4 flex items-center">{trailing}</span>
      </div>
    )
  }
  return (
    <Link
      {...link}
      className={cn(
        "flex h-9 scroll-mt-32 items-center gap-3 border-b border-border-subtle text-ui transition-colors duration-100 ease-snappy",
        insets[inset].row,
        rowHighlight,
      )}
    >
      <span className="flex w-4 shrink-0 justify-center">
        <PriorityIcon priority={ticket.priority} standalone />
      </span>
      <span className="flex w-16 shrink-0">
        <Key value={ticket.key} />
      </span>
      <span className="flex w-4 shrink-0 justify-center">
        <StatusIcon
          category={ticket.status.category}
          name={ticket.status.name}
          {...(ticket.status.step && { step: ticket.status.step })}
        />
      </span>
      <span
        className={cn(
          "min-w-0 flex-1 truncate",
          ticket.closed ? "text-muted-foreground" : "text-foreground",
        )}
      >
        {ticket.title}
      </span>
      {/* The chips keep their slots when absent, so Blocked lines up down the list whether or not
          an Epic follows it. The Epic's fits a key; a longer title widens its own row's alone. */}
      <span className="flex w-18 shrink-0 justify-end">
        {ticket.blocked && <BlockedChip {...(ticket.blockers && { blockers: ticket.blockers })} />}
      </span>
      {!hideEpic && (
        <span className="flex min-w-20 shrink-0 justify-end">
          {ticket.epic && (
            <EpicChip
              epicKey={ticket.epic.key}
              {...(ticket.epic.title !== undefined && { title: ticket.epic.title })}
            />
          )}
        </span>
      )}
      <span className="flex w-6 shrink-0 justify-center">
        {ticket.size && <SizeChip size={ticket.size.size} name={ticket.size.name} />}
      </span>
      <span className="flex w-4 shrink-0 justify-center">
        {ticket.type !== "story" && <TypeIcon type={ticket.type} standalone />}
      </span>
      <span className="flex w-14 shrink-0 justify-end">
        <AvatarStack people={ticket.assignees} size={20} on={insets[inset].on} />
      </span>
    </Link>
  )
}

function LinkRow({
  href,
  state,
  opens = "in-place",
  ticket,
  trailing,
}: {
  href?: string
  state?: unknown
  opens?: Opens
  ticket: LinkedTicket
  trailing?: ReactNode
}) {
  const readable = ticket.title !== undefined
  const content = (
    <>
      {ticket.status && (
        <span className="flex w-4 shrink-0 justify-center">
          <StatusIcon
            category={ticket.status.category}
            name={ticket.status.name}
            {...(ticket.status.step && { step: ticket.status.step })}
          />
        </span>
      )}
      <Key value={ticket.key} />
      {readable && <span className="min-w-0 flex-1 truncate text-foreground">{ticket.title}</span>}
    </>
  )
  const row = "flex h-8 min-w-0 flex-1 items-center gap-2 px-2 touch:h-11"
  return (
    <div
      className={cn(
        "group/row flex items-center rounded-md transition-colors duration-100 ease-snappy",
        href && "has-[[data-row]:focus-visible]:bg-accent hover:bg-accent",
      )}
    >
      {href && readable ? (
        <Link
          to={href}
          {...(state !== undefined && { state })}
          {...targets[opens]}
          data-row
          className={cn(row, "rounded-md outline-none data-highlighted:bg-accent")}
        >
          {content}
        </Link>
      ) : (
        <div className={row}>{content}</div>
      )}
      {trailing && <span className="mr-1 flex shrink-0 items-center">{trailing}</span>}
    </div>
  )
}
